import { NavLink } from 'react-router-dom'
import { cn } from '../lib/utils'
import { Icon, type IconName } from './ui/icons'

interface NavItem {
  path: string
  /** Visible short label (≤6 chars). Readable 12px on phone. */
  label: string
  /** Full destination name. Used for the accessible name + tooltip. */
  fullName: string
  icon: IconName
  matchPrefix?: string
}

/**
 * Compact phone bottom navigation. Four core destinations — Home,
 * Bills, Reps (Representatives), Actions. All other
 * routes remain reachable from the drawer (`Sidebar` slides in over the top).
 *
 * Visible labels are intentionally short (3–6 chars) so each tab fits its
 * 1/4 column at 320px without truncation. The accessible name and tooltip
 * are the full destination so screen-reader and tooltip users get the
 * unambiguous name.
 *
 * Render only below the lg breakpoint. Active route is communicated via
 * `aria-current="page"` and the filled icon background so screen readers
 * and sighted users both see the current location. Nested routes (e.g.
 * `/representatives/123`) activate the parent tab because NavLink matches
 * by prefix when `end={false}`.
 */
const ITEMS: NavItem[] = [
  { path: '/', label: 'Home', fullName: 'Home', icon: 'dashboard' },
  { path: '/bills', label: 'Bills', fullName: 'Bills', icon: 'bills' },
  { path: '/representatives?mine=true', label: 'Reps', fullName: 'Representatives', icon: 'representatives' },
  { path: '/actions', label: 'Actions', fullName: 'Actions', icon: 'actions' },
]

export default function MobileNav({ hidden = false }: { hidden?: boolean }) {
  if (hidden) return null
  return (
    <nav
      aria-label="Quick navigation"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      <ul className="mx-auto grid max-w-md grid-cols-4">
        {ITEMS.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.path === '/'}
              aria-label={item.fullName}
              title={item.fullName}
              className={({ isActive }) => cn(
                'flex min-h-[56px] flex-col items-center justify-center gap-1 px-1 py-2 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500',
                isActive ? 'text-[color:var(--nav-selected)]' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-md',
                       isActive ? 'bg-[var(--nav-selected-bg)]' : 'bg-transparent',
                    )}
                  >
                    <Icon
                      name={item.icon}
                       className={cn('h-5 w-5', isActive ? 'text-[color:var(--nav-selected)]' : 'text-slate-500')}
                    />
                  </span>
                  <span className="truncate">{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
